import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import {
  TimelineShift,
  GeoLocation,
  OrganizationalUnit,
  TimelineShiftWithActivities,
} from '@wilson/interfaces';
import { LocationsStoreService } from '@wilson/locations';
import {
  DropZoneEventThrottlerDirective,
  GanttDateToPositionPxPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { CreateNewViaDragAndDropService } from '@wilson/shift-timeline/services';
import {
  TimelineRemoveShiftFromUserV2,
  TimelineStaysSuggestionService,
} from '@wilson/shift-timeline/state';
import { addHours, startOfDay } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { NewShiftDropZoneHelperService } from '../new-shift-drop-zone-component-v2/new-shift-drop-zone-helper.service';
import { TimelineCellHighlightService } from '@wilson/shift-timeline/assignments-gantt-chart';

@Component({
  selector: 'wilson-new-shift-drop-zone-cell-view',
  styleUrls: ['./new-shift-drop-zone-cell-view.component.scss'],
  imports: [],
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GanttDateToPositionPxPipe],
})
export class NewShiftDropZoneCellViewComponent
  extends DropZoneEventThrottlerDirective
  implements OnInit
{
  private store = inject(Store);
  private locationsStoreService = inject(LocationsStoreService);
  protected timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private newShiftDropZoneHelperService = inject(NewShiftDropZoneHelperService);
  private timelineCellHighlightService = inject(TimelineCellHighlightService);

  @Input() userId!: string | undefined;

  private organizationalUnit!: OrganizationalUnit | undefined;
  private location!: GeoLocation | null;
  private mockShiftWithoutActivities!: TimelineShift;
  private mockShift!: TimelineShiftWithActivities;

  ngOnInit(): void {
    this.organizationalUnit = this.store.selectSnapshot(
      AuthState.organizationalUnit,
    );
    if (this.organizationalUnit?.locationId) {
      firstValueFrom(
        this.locationsStoreService.getLocationFromCache(
          this.organizationalUnit.locationId as string,
        ),
      ).then((location) => {
        this.location = location || null;
      });
    }
  }

  protected override onDragOverHandler(offsetX: number) {
    this.timelineCellHighlightService.clearCellsFromHighlight();
    let shiftStartDate =
      this.newShiftDropZoneHelperService.generateDateFromOffsetX(offsetX);
    shiftStartDate = addHours(startOfDay(shiftStartDate), 8);
    const processingResult =
      this.newShiftDropZoneHelperService.handleOnDragOverProcess({
        shiftStartDate,
        offsetX: offsetX,
        organizationalUnit: this.organizationalUnit,
        userId: this.userId,
        location: this.location,
      });

    if (processingResult) {
      this.mockShift = processingResult.mockShift;
      this.mockShiftWithoutActivities =
        processingResult.mockShiftWithoutActivities;
      this.timelineCellHighlightService.setCellToHighlight(
        this.userId as string,
        processingResult.mockShift.startDatetime,
      );
    }
  }

  protected override onDragLeaveHandler() {
    if (this.userId) {
      this.store.dispatch(
        new TimelineRemoveShiftFromUserV2({
          userId: this.userId,
          shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
        }),
      );
    }
    this.timelineCellHighlightService.clearCellsFromHighlight();
  }

  protected override onDropHandler() {
    this.newShiftDropZoneHelperService.handleOnDropProcess({
      currentDemarcatorRange: null,
      mockShift: this.mockShift,
      mockShiftWithoutActivities: this.mockShiftWithoutActivities,
      userId: this.userId,
    });
    this.timelineCellHighlightService.clearCellsFromHighlight();
  }
}
